import React, { useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useCheckoutStore } from "../store/checkout-store";
import { useTranslation } from "react-i18next";

/**
 * 3D Secure Modal Component
 * Zero Props Pattern - Uses checkout store directly for state management
 * Webview-based implementation with postMessage event handling
 */
export const Checkout3DSModal: React.FC = () => {
  const { t } = useTranslation();
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Get state and actions directly from store
  const { is3DSModalOpen, threeDSHtmlContent, close3DSModal } =
    useCheckoutStore();

  // Load 3DS HTML content into iframe
  // Wait for modal animation to complete before loading content
  useEffect(() => {
    if (!is3DSModalOpen || !threeDSHtmlContent) {
      return;
    }

    console.log("📝 Modal opened, waiting for animation to complete...");
    console.log("📄 HTML content length:", threeDSHtmlContent.length);

    // Wait for modal animation to complete (framer-motion spring animation ~300ms)
    // Add extra buffer for iframe rendering
    const animationDelay = 600; // 600ms should be enough for animation + iframe mount

    const timer = setTimeout(() => {
      if (!iframeRef.current) {
        console.error("❌ Iframe ref is null after delay");
        return;
      }

      const iframe = iframeRef.current;
      console.log("✅ Animation delay completed, loading iframe content...");

      // Inject mobile-friendly viewport and postMessage handler
      const enhancedHtmlContent = threeDSHtmlContent.replace(
        "</head>",
        `<head>
          <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no">
          <style>
            body { 
              margin: 0; 
              padding: 16px; 
              font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
              -webkit-font-smoothing: antialiased;
              -moz-osx-font-smoothing: grayscale;
            }
            * { 
              box-sizing: border-box; 
            }
            input, button, select, textarea {
              font-size: 16px !important; /* Prevent zoom on iOS */
            }
            @media (max-width: 768px) {
              body { padding: 12px; }
              input, button { 
                width: 100% !important; 
                margin-bottom: 8px !important;
              }
            }
          </style>
          <script>
            // PostMessage handler for checkout result
            (function() {
              // Listen for form submissions and URL changes
              window.addEventListener('load', function() {
                console.log('🔍 3DS page loaded, setting up postMessage handler...');
                
                // Monitor form submissions
                var form = document.querySelector('form');
                if (form) {
                  form.addEventListener('submit', function(e) {
                    console.log('📤 Form submitted');
                    // Form will navigate, we'll catch the result via URL monitoring
                  });
                }
                
                // Monitor URL changes (for success/failure redirects)
                var lastUrl = window.location.href;
                var urlCheckInterval = setInterval(function() {
                  try {
                    var currentUrl = window.location.href;
                    if (currentUrl !== lastUrl) {
                      lastUrl = currentUrl;
                      console.log('🔍 URL changed:', currentUrl);
                      
                      // Check for success/failure URLs
                      if (currentUrl.includes('3ds-verify/success')) {
                        console.log('✅ Success URL detected');
                        window.parent.postMessage({
                          type: 'checkout_result',
                          result: 'success',
                          orderGroupId: extractOrderIdFromUrl(currentUrl)
                        }, '*');
                        clearInterval(urlCheckInterval);
                      } else if (currentUrl.includes('3ds-verify/failure')) {
                        console.log('❌ Failure URL detected');
                        window.parent.postMessage({
                          type: 'checkout_result',
                          result: 'failure',
                          message: '3D Secure verification failed'
                        }, '*');
                        clearInterval(urlCheckInterval);
                      } else if (currentUrl.includes('/checkout/error')) {
                        console.log('⚠️ Error URL detected');
                        try {
                          var urlObj = new URL(currentUrl);
                          var message = urlObj.searchParams.get('message') || 'Payment error';
                          var code = urlObj.searchParams.get('code') || '';
                          window.parent.postMessage({
                            type: 'checkout_result',
                            result: 'failure',
                            code: code,
                            message: message
                          }, '*');
                        } catch (e) {
                          window.parent.postMessage({
                            type: 'checkout_result',
                            result: 'failure',
                            message: 'Payment error'
                          }, '*');
                        }
                        clearInterval(urlCheckInterval);
                      }
                    }
                  } catch (e) {
                    // Cross-origin error - normal
                  }
                }, 300);
                
                // Helper function to extract order ID from URL
                function extractOrderIdFromUrl(url) {
                  try {
                    var match = url.match(/3ds-verify\/success\/([^\/]+)/);
                    return match ? match[1] : null;
                  } catch (e) {
                    return null;
                  }
                }
              });
            })();
          </script>
        </head>`,
      );

      // Wait for iframe to be ready, then write content
      const handleIframeLoad = () => {
        console.log("✅ Iframe ready, writing content...");
        try {
          const iframeDoc =
            iframe.contentDocument || iframe.contentWindow?.document;

          if (iframeDoc) {
            console.log("✅ Iframe document accessible");
            iframeDoc.open();
            iframeDoc.write(enhancedHtmlContent);
            iframeDoc.close();
            console.log("✅ HTML content written to iframe");
          } else {
            console.error("❌ Cannot access iframe document");
          }
        } catch (err) {
          console.error("❌ Error writing to iframe:", err);
        }
      };

      // First, set iframe to blank page to ensure it's ready
      iframe.src = "about:blank";

      // Wait for iframe to load blank page, then write content
      iframe.addEventListener("load", handleIframeLoad, { once: true });
    }, animationDelay);

    return () => {
      clearTimeout(timer);
    };
  }, [is3DSModalOpen, threeDSHtmlContent]);

  return (
    <Dialog open={is3DSModalOpen} onOpenChange={close3DSModal}>
      <DialogContent
        open={is3DSModalOpen}
        className="w-[95vw] sm:w-[90vw] md:w-[85vw] lg:max-w-4xl h-[65vh] sm:h-[90vh] p-0 z-[1200] !bg-white dark:!bg-gray-900 !backdrop-blur-none !backdrop-saturate-100 flex flex-col m-2 sm:m-4"
      >
        <DialogHeader className="p-3 sm:p-4 md:p-6 pb-3 sm:pb-4 flex-shrink-0 border-b border-gray-200 dark:border-gray-700">
          <DialogTitle className="text-[16px] sm:text-[18px] md:text-[20px] font-semibold text-[#121926] dark:text-gray-100">
            {t("checkout.threeDSVerification")}
          </DialogTitle>
        </DialogHeader>

        {/* 3DS Iframe Container */}
        <div className="relative w-full flex-1 overflow-auto bg-white dark:bg-gray-900">
          {threeDSHtmlContent ? (
            <iframe
              ref={iframeRef}
              title="3D Secure Verification"
              className="w-full h-full min-h-[400px] border-0 bg-white"
              sandbox="allow-scripts allow-same-origin allow-forms allow-top-navigation allow-popups allow-modals"
              style={{
                minWidth: "100%",
                width: "1px",
              }}
            />
          ) : (
            <div className="flex items-center justify-center h-full min-h-[400px]">
              <div className="text-center px-4">
                <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-[#6172f3] mx-auto mb-4"></div>
                <p className="text-xs sm:text-sm text-muted-foreground">
                  {t("checkout.loading3DS")}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Info Text */}
        <div className="px-3 sm:px-4 md:px-6 pb-3 sm:pb-4 md:pb-6 flex-shrink-0 border-t border-gray-200 dark:border-gray-700 pt-3 sm:pt-4">
          <p className="text-[11px] sm:text-[12px] text-[#697586] dark:text-gray-400 text-center">
            {t("checkout.threeDSInfo")}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};
